// LoginPage — /login.
//
// Unified login (SEN-634): the self-built email/password + Google/Apple/LINE
// dashboard login has been retired. /login now redirects straight to SENTRY
// Accounts (accounts.sentry.red) — NEXUS is a relying party of the central IdP.
// Auto-redirect happens only on a genuine "arriving unauthenticated" state.
// After an explicit logout (?logged_out=1) or a denied attempt (?error=…) we
// show a manual button instead — an immediate redirect against a still-live IdP
// session would silently re-mint a NEXUS session and defeat sign-out / account
// switching. (LINE LIFF / binding-code — external terminal identity — are a
// separate surface and unaffected.)

import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, ShieldCheck } from "lucide-react";
import AuthLayout from "./AuthLayout.js";
import { accountsInitUrl, friendlyAuthError } from "../../lib/auth.js";

const DEFAULT_RETURN = "/dashboard/";

export default function LoginPage(): React.ReactElement {
  const [search] = useSearchParams();
  const returnTo = search.get("return_to") || DEFAULT_RETURN;
  const [error, setError] = useState<string | null>(null);
  // SessionExpiryWatcher redirects here with ?reason=session_expired on a 401.
  const expiredNotice = search.get("reason") === "session_expired";
  // performLogout() lands here with ?logged_out=1 after an explicit sign-out.
  const loggedOut = search.get("logged_out") === "1";
  // Suppress the auto-redirect for logout / denied-attempt; those must not
  // silently re-authenticate against a still-live Accounts session.
  const manual = Boolean(error) || loggedOut;

  useEffect(() => {
    const e = search.get("error");
    if (e) {
      // A previous Accounts attempt was denied — surface it, don't loop.
      setError(friendlyAuthError({ error: e }));
    } else if (search.get("logged_out") !== "1") {
      // No local login any more — go straight to SENTRY Accounts.
      window.location.href = accountsInitUrl(returnTo);
    }
  }, [search, returnTo]);

  return (
    <AuthLayout title="登入" subtitle="使用 SENTRY 帳號繼續使用 NEXUS">
      {expiredNotice && !error && (
        <div
          role="status"
          className="mb-3 flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning"
        >
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>您的登入已逾時，請重新登入。完成後會自動回到剛才的頁面。</span>
        </div>
      )}
      {loggedOut && !error && (
        <div
          role="status"
          className="mb-3 flex items-start gap-2 rounded-md border border-border bg-bg-subtle px-3 py-2 text-sm text-text-secondary"
        >
          <ShieldCheck size={16} className="mt-0.5 shrink-0" />
          <span>您已登出。</span>
        </div>
      )}
      {error ? (
        <div className="flex flex-col gap-4">
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
          >
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
          <button
            type="button"
            className="btn-primary w-full"
            onClick={() => {
              window.location.href = accountsInitUrl(returnTo);
            }}
          >
            <ShieldCheck size={16} />
            重新以 SENTRY 帳號登入
          </button>
        </div>
      ) : manual ? (
        <button
          type="button"
          className="btn-primary w-full"
          onClick={() => {
            window.location.href = accountsInitUrl(returnTo);
          }}
        >
          <ShieldCheck size={16} />
          以 SENTRY 帳號登入
        </button>
      ) : (
        <p className="text-center text-sm text-text-secondary">導向 SENTRY 帳號登入…</p>
      )}
    </AuthLayout>
  );
}
